/**
 * mk-beach-footer — the ball's roll, spin and settle.
 *
 * A `Component` subclass registered as a custom element, so it survives
 * `morphSection()`: the element instance is preserved (morph runs
 * `childrenOnly: true`), the engine subscription stays live and is not
 * duplicated, and `Component`'s own MutationObserver re-syncs `this.refs`.
 * `updatedCallback` is kept because it is the documented hook, but nothing
 * depends on it — it was measured at task 4 and does not fire.
 *
 * SHORE_D IS NOT DUPLICATED HERE. The Liquid owns it and emits it into the
 * path's `d`, into `--mk-shore-path` and into the grain mask. This script
 * reads it back out of the custom property purely to measure path length for
 * the rotation, so there is exactly one copy of the curve on the page.
 *
 * NEVER READS window.scrollY — it is permanently 0 at >=990px, where
 * `.page-wrapper` scrolls instead of the document. All position comes from
 * `getBoundingClientRect()`, which is correct on both sides of the breakpoint.
 */

import { Component } from '@theme/component';
import { subscribe, observe } from '@theme/mk-scroll-engine';

const SHORE_VIEWBOX_WIDTH = 1440;
const SHORE_VIEWBOX_HEIGHT = 120;

/** Settle: diminishing rocks at the destination. Not bouncy. */
const SETTLE_STEPS = [
  { at: 50, deg: 8 },
  { at: 150, deg: -3 },
  { at: 250, deg: 1 },
  { at: 400, deg: 0 },
];
const SETTLE_MS = 400;

/** Scroll containers rarely land exactly on their maximum. */
const SETTLE_TOLERANCE_PX = 2;

/**
 * Applied to the sections BELOW this one for the duration of a single
 * measurement, then removed. Its rule lives in mk-beach-footer.css and forces
 * `content-visibility: visible` on the subtree — see `#measureBelow()` for why
 * that is necessary. Added and removed inside one synchronous block, so no
 * frame is ever painted with it on.
 */
const MEASURING_CLASS = 'mk-measuring-extent';

/**
 * How far ahead of the footer the roll begins, as a fraction of the viewport.
 *
 * A full viewport (1.0) was far too generous — the ball had already settled
 * before the shoreline was properly in view, because most of the roll happened
 * while the footer was still below the fold. 0.4 and 0.2 were each still early.
 * At 0.1 the roll begins with the footer only 90px below the fold, so it is
 * barely underway as the shoreline appears and finishes with the ball at the
 * umbrella.
 *
 * Do not take this to 0. The span would collapse to the footer's own height
 * (259px at a 900px viewport) and the ball snaps from start to rest in one
 * scroll step — the defect task 17 fixed.
 *
 * The roll is rAF-driven off `getBoundingClientRect()`, so it is continuous
 * rather than quantised: a shorter window does not step, it just travels
 * further per pixel of scroll. The thing that can read as stepping is the
 * INPUT — one mouse-wheel tick is roughly 100px, so the shorter the window the
 * more of the roll a single tick consumes. That is the number to watch, not
 * the sample spacing.
 *
 * Measured at a 900px viewport, 259px footer:
 *   1.0 -> span 1159px   0.4 -> span 619px   0.2 -> span 439px   0.1 -> 349px
 *   per 100px wheel tick: 7.4%        13.9%        19.6%        24.6% of the roll
 */
const LEAD_IN_RATIO = 0.1;

class MkBeachFooter extends Component {
  #unsubscribe = null;
  #unobserve = null;
  #settleTimers = [];
  #visible = false;
  #settled = false;
  #pathLength = 1;
  #visualDiameter = 80;
  #widthRatio = 1;
  #restPercent = 88;
  /** Distance from this section's top down to the shoreline's LOWEST point. */
  #shoreOffset = 0;
  /** Scroll left below this section before the page ends (Horizon's footer). */
  #belowPx = 0;
  /**
   * Cleared whenever the gate opens or the geometry is re-measured; set once
   * the extent has been re-checked at the last moment before the roll starts.
   * See the confirmation step in `#frame()`.
   */
  #extentConfirmed = false;

  connectedCallback() {
    super.connectedCallback();

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Measure FIRST, always. Reduced motion disables MOTION, not layout: the
    // ball still has to be scaled onto the curve at its resting point. Bailing
    // out before this left the 1440-unit container unscaled, so the ball sat
    // outside the clipped viewport and vanished entirely — caught by
    // screenshot, not by measurement, since every computed value read fine.
    this.#measure();

    // Nothing further is registered under reduced motion. The ball holds the
    // resting `offset-distance` and the scene is static — disabled, not
    // shortened.
    if (this.reducedMotion) return;

    this.onResize = () => this.#measure();
    window.addEventListener('resize', this.onResize, { passive: true });

    // IO gating through the engine, so the root is correct on both sides of
    // the 990px breakpoint and is rebuilt when it is crossed.
    //
    // The rootMargin lead-in matters: the roll spans roughly 1.2 viewports of
    // scroll, but the footer itself only becomes visible in the last fraction
    // of that. Without the margin the gate held #frame off until the ball was
    // already meant to be arriving, so it sat at its resting position and then
    // jumped. One viewport of lead-in lets the roll be underway — off-screen
    // and free — by the time the footer appears.
    this.#unobserve = observe(
      this,
      (isIntersecting) => {
        this.#visible = isIntersecting;
        // The compositor hint tracks the gate, so it is never carried while
        // the ball cannot move.
        this.classList.toggle('is-ball-rolling', isIntersecting);

        // Re-measure the roll extent HERE, not only at connect.
        //
        // Horizon's footer sits below this section under
        // `contain: content; content-visibility: auto`. At connect the page is
        // at the top, that subtree is skipped, and its height reports as the
        // containment placeholder — measured 60px against a real 190px. The
        // roll extent came out 254.6px instead of 385px and the window barely
        // changed. (The same 60px discrepancy is what made an earlier
        // `scrollHeight - clientHeight` attempt look wrong; it was this, not
        // scrollHeight.)
        //
        // By the time the gate opens the footer is within a viewport of the
        // fold, `content-visibility` has un-skipped it, and the rect is honest.
        // Measured once per approach, not per frame — forcing layout on a
        // contained subtree every frame is exactly what that containment
        // exists to avoid.
        if (isIntersecting) {
          this.#extentConfirmed = false;
          this.#measureRollExtent();
        }
      },
      // top right bottom left — the footer approaches from BELOW, so it is the
      // BOTTOM margin that must be expanded. Expanding the top instead left the
      // gate closed until the footer had almost arrived.
      { rootMargin: '0px 0px 100% 0px' }
    );

    this.#unsubscribe = subscribe(() => this.#frame());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unsubscribe?.();
    this.#unobserve?.();
    this.#unsubscribe = null;
    this.#unobserve = null;
    window.removeEventListener('resize', this.onResize);
    this.#clearSettle();
  }

  updatedCallback() {
    // Documented as firing after a morph; measured at task 4 as never firing.
    // Kept because other morph callers may use it, load-bearing on nothing.
    if (!this.reducedMotion) this.#measure();
  }

  /**
   * Geometry comes from the tokens via computed style, so CSS and this script
   * can never disagree. `--mk-ball-base` is the intrinsic box and the centring
   * offset is always exactly half of it; `--mk-ball-visual` is overridden at
   * the 768px breakpoint in the stylesheet, so reading it here means the
   * breakpoint is declared exactly once, in CSS.
   */
  #measure() {
    const { ballContainer, ballPath } = this.refs;
    if (!ballContainer || !ballPath) return;

    const styles = getComputedStyle(this);
    const readToken = (name, fallback) => {
      const parsed = parseFloat(styles.getPropertyValue(name));
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    const ballBase = readToken('--mk-ball-base', 44);
    this.#visualDiameter = readToken('--mk-ball-visual', 80);
    this.#restPercent = readToken('--mk-ball-rest', 88);

    const width = this.getBoundingClientRect().width;
    this.#widthRatio = width / SHORE_VIEWBOX_WIDTH;
    ballContainer.style.transform = `scale(${this.#widthRatio})`;

    // Counter-scale the ball so its rendered diameter is --mk-ball-visual
    // regardless of how far the 1440-unit container was scaled.
    const counterScale = this.#visualDiameter / (ballBase * this.#widthRatio);
    const centringOffset = (ballBase / -2) * counterScale;
    ballPath.style.transform = `scale(${counterScale})`;
    ballPath.style.marginTop = `${centringOffset}px`;

    this.#pathLength = this.#measurePathLength(styles);
    this.#measureRollExtent();
    this.#extentConfirmed = false;

    // Hands the ball over from the CSS-only parked position to the offset-path
    // system. Added only once the geometry above is applied, so there is no
    // frame where the ball is on the curve but unscaled.
    this.classList.add('is-ball-ready');
  }

  /**
   * The roll spans the SAND, not this section.
   *
   * Measured at 1200x900: this section is 259px, but the sand the reader
   * actually sees runs from the shoreline's lowest point (64px down the wave)
   * to the bottom of the page — 195px of it here plus Horizon's 190px footer
   * below, 385px in all. Ending the roll at this section's bottom left those
   * last 190px of scroll unused, with the ball already parked, and squeezed the
   * whole roll into 2.6 wheel ticks.
   *
   * Both numbers come from rects. `scrollHeight - clientHeight` was tried
   * earlier and reported ~60px past where the container actually stops, which
   * ended the roll at 94% and never settled.
   */
  #measureRollExtent() {
    const rect = this.getBoundingClientRect();

    // Where the shoreline's lowest point sits inside this section. The curve
    // does not reach the viewBox floor — its control points overshoot to 120
    // but it bottoms out at 77.32 — so this is sampled, not assumed.
    const wave = this.querySelector('.mk-beach-footer__top');
    let shoreOffset = 0;
    if (wave) {
      const waveRect = wave.getBoundingClientRect();
      const styles = getComputedStyle(this);
      const d = styles.getPropertyValue('--mk-shore-path').trim().replace(/^["']|["']$/g, '');
      let lowestUnit = SHORE_VIEWBOX_HEIGHT / 2;
      if (d) {
        try {
          const probe = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          probe.setAttribute('d', d);
          const total = probe.getTotalLength();
          if (total > 0) {
            lowestUnit = 0;
            for (let i = 0; i <= 240; i++) {
              const y = probe.getPointAtLength((total * i) / 240).y;
              if (y > lowestUnit) lowestUnit = y;
            }
          }
        } catch {
          /* keep the midline fallback */
        }
      }
      shoreOffset =
        waveRect.top - rect.top + lowestUnit * (waveRect.height / SHORE_VIEWBOX_HEIGHT);
    }
    this.#shoreOffset = Math.max(0, Math.min(shoreOffset, rect.height));

    this.#belowPx = this.#measureBelow();
  }

  /**
   * Everything still to scroll past below this section — Horizon's footer
   * sections. Summed from rects so a zero-height section (the cart drawer)
   * contributes nothing.
   *
   * A PLAIN RECT READ HERE IS NOT SAFE, and this is the whole reason the method
   * exists. Horizon's `.footer-content` carries
   * `contain: content; content-visibility: auto`. While that subtree is skipped
   * its box reports the containment placeholder — measured 60px against a real
   * 190.4px on a fresh, never-scrolled page — and `getBoundingClientRect()`,
   * `offsetHeight` and `scrollHeight` on the section ALL return the lie.
   * Reading a descendant's rect first does not help either: re-reading the
   * section afterwards still gave 60. Nor does the scroll container's own
   * `scrollHeight`, which is short by the same 130px while the footer is
   * skipped — that, not `scrollHeight` itself, is what made an earlier attempt
   * with it look wrong.
   *
   * The one thing that does work is asking the subtree to stop being skipped
   * for the duration of the read. `content-visibility: visible` alone is
   * enough; `contain: content` does not include SIZE containment, so it never
   * zeroed anything on its own. Verified at every step on a page that had never
   * been scrolled, since `contain-intrinsic-size: auto` remembers the last real
   * size and will happily hand back the right answer once the footer has been
   * on screen even once — which is exactly what makes this bug invisible in
   * casual testing.
   *
   * Cost is one forced layout of the footer subtree, measured at 7.1ms cold.
   * That is why this is called on approach and at the start of the roll, and
   * never per frame.
   *
   * The class goes on every sibling first and comes off every sibling last, so
   * the browser does one style recalc and one layout rather than one per
   * section, and no paint can occur in between.
   */
  #measureBelow() {
    const host = this.closest('.shopify-section') || this;

    const siblings = [];
    for (let node = host.nextElementSibling; node; node = node.nextElementSibling) {
      siblings.push(node);
    }
    if (!siblings.length) return 0;

    for (const node of siblings) node.classList.add(MEASURING_CLASS);

    let below = 0;
    try {
      for (const node of siblings) below += node.getBoundingClientRect().height;
    } finally {
      // `finally` so a throw mid-measurement cannot leave Horizon's footer
      // permanently un-skipped, which would quietly undo its performance work.
      for (const node of siblings) node.classList.remove(MEASURING_CLASS);
    }

    return Math.max(0, below);
  }

  /** Reads the curve back out of --mk-shore-path so there is one copy of it. */
  #measurePathLength(styles) {
    const raw = styles.getPropertyValue('--mk-shore-path').trim().replace(/^["']|["']$/g, '');
    if (!raw) return this.#pathLength;
    try {
      const probe = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      probe.setAttribute('d', raw);
      const length = probe.getTotalLength();
      return Number.isFinite(length) && length > 0 ? length : this.#pathLength;
    } catch {
      return this.#pathLength;
    }
  }

  #frame() {
    if (!this.#visible) return;

    const { ballPath, ballSpin } = this.refs;
    if (!ballPath || !ballSpin) return;

    // Progress is measured from the SHORELINE, not from this section's top, and
    // runs to the bottom of the page rather than to this section's bottom.
    //
    // The tracked point is the shoreline's lowest pixel; the window is the sand
    // below it — this section's remainder plus Horizon's footer — with the
    // lead-in in front. So the roll now uses the 190px of scroll that sat idle
    // beneath it, and finishes as the page ends instead of 190px early.
    //
    // shoreY = viewport + leadIn      -> 0   (shoreline a lead-in below the fold)
    // shoreY = viewport - rollExtent  -> 1   (page end)
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const leadIn = viewportHeight * LEAD_IN_RATIO;

    const measure = () => {
      const rect = this.getBoundingClientRect();
      const shoreY = rect.top + this.#shoreOffset;
      const rollExtent = Math.max(1, rect.height - this.#shoreOffset + this.#belowPx);
      const span = Math.max(1, leadIn + rollExtent);
      return {
        span,
        progress: Math.max(0, Math.min(1, (viewportHeight + leadIn - shoreY) / span)),
      };
    };

    let { span, progress } = measure();

    // One last look at the geometry, taken at the instant the roll begins and
    // never again while it runs.
    //
    // `#measureBelow()` already reads through `content-visibility`, so the
    // value taken on approach is honest. This guards the other way the extent
    // can go stale: the footer's own layout settling AFTER the gate opened —
    // a webfont swapping, the logo decoding, a Shopify app injecting a row.
    //
    // The trigger is deliberately `progress > 0` rather than a frame count or a
    // timer. Changing the span mid-roll would move the ball backwards, which is
    // the one thing this must never do; at the instant progress first leaves
    // zero the correction is worth a fraction of a percent, so re-deriving it
    // here is invisible by construction. After that the span is frozen for the
    // rest of the approach and monotonicity is guaranteed by arithmetic rather
    // than by hoping the measurement was right.
    if (!this.#extentConfirmed && progress > 0) {
      this.#extentConfirmed = true;
      this.#measureRollExtent();
      ({ span, progress } = measure());
    }

    const remaining = (1 - progress) * span;

    const percent = progress * this.#restPercent;

    // A couple of pixels of tolerance. Scroll containers rarely land exactly on
    // their maximum — measured 0.9995 at the true end of the page — and an
    // exact `>= 1` meant the settle never fired and the ball stopped a
    // fraction short of its resting point.
    if (remaining <= SETTLE_TOLERANCE_PX) {
      if (!this.#settled) this.#settle();
      return;
    }

    // Scrolling back up releases the settle so the roll runs in reverse.
    if (this.#settled) this.#clearSettle();

    ballPath.style.offsetDistance = `${percent}%`;
    ballSpin.style.transform = `rotate(${this.#rotationFor(percent)}deg)`;
  }

  /** Spin rate must match travel: theta = distance / radius. */
  #rotationFor(percent) {
    const scaledLength = this.#pathLength * this.#widthRatio;
    const distancePx = (percent / 100) * scaledLength;
    const radius = this.#visualDiameter / 2;
    return (distancePx / radius) * (180 / Math.PI);
  }

  #settle() {
    const { ballPath, ballSpin } = this.refs;
    if (!ballPath || !ballSpin) return;

    this.#settled = true;
    ballPath.style.offsetDistance = `${this.#restPercent}%`;

    const base = this.#rotationFor(this.#restPercent);
    ballSpin.style.transition = `transform ${SETTLE_MS}ms cubic-bezier(0.25, 1, 0.5, 1)`;

    for (const { at, deg } of SETTLE_STEPS) {
      this.#settleTimers.push(
        setTimeout(() => {
          ballSpin.style.transform = `rotate(${base + deg}deg)`;
        }, at)
      );
    }

    this.#settleTimers.push(
      setTimeout(() => {
        ballSpin.style.transition = '';
      }, SETTLE_MS + 60)
    );
  }

  #clearSettle() {
    for (const timer of this.#settleTimers) clearTimeout(timer);
    this.#settleTimers = [];
    this.#settled = false;
    const { ballSpin } = this.refs;
    if (ballSpin) ballSpin.style.transition = '';
  }
}

if (!customElements.get('mk-beach-footer')) {
  customElements.define('mk-beach-footer', MkBeachFooter);
}
